import { type FormEvent, useState } from "react";

import {
  type DispositionCategory,
  type Inventory,
  type InventoryQuery,
  getInventory,
  manifestExportUrl,
} from "./intakeClient";

const CATEGORIES: DispositionCategory[] = [
  "processed",
  "duplicate",
  "excluded",
  "failed",
  "held",
  "unresolved",
];
const PAGE = 100;

function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : String(caught);
}

function initialScope(): InventoryQuery {
  const params = new URLSearchParams(typeof window === "undefined" ? "" : window.location.search);
  return {
    batch_id: params.get("batch") ?? undefined,
    engagement_id: params.get("engagement") ?? undefined,
    run_id: params.get("run") ?? undefined,
  };
}

function size(bytes: number | null): string {
  if (bytes === null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MiB`;
}

/** Corpus inventory (DESIGN-SPEC 13, 15.1): every item of a batch or Corpus Run with one
 * disposition category. Counts always cover the whole scope; filters only choose the rows. */
export function SourcesInventoryPage() {
  const [scope, setScope] = useState<InventoryQuery>(initialScope);
  const [filters, setFilters] = useState<InventoryQuery>({});
  const [offset, setOffset] = useState(0);
  const [inventory, setInventory] = useState<Inventory | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load(nextScope: InventoryQuery, nextFilters: InventoryQuery, nextOffset: number) {
    setError(null);
    try {
      setInventory(
        await getInventory({ ...nextScope, ...nextFilters, offset: nextOffset, limit: PAGE }),
      );
      setScope(nextScope);
      setFilters(nextFilters);
      setOffset(nextOffset);
    } catch (caught) {
      setError(message(caught));
    }
  }

  function onScope(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim() || undefined;
    void load(
      { batch_id: text("batch"), engagement_id: text("engagement"), run_id: text("run") },
      {},
      0,
    );
  }

  function onFilter(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const pick = <T extends string>(name: string) => (String(form.get(name) ?? "") || undefined) as T | undefined;
    void load(
      scope,
      {
        category: pick<DispositionCategory>("category"),
        entry_status: pick("entry_status"),
        access_classification: pick("access_classification"),
        pending_review: form.get("pending_review") === "on" ? true : undefined,
        retryable: form.get("retryable") === "on" ? true : undefined,
        q: String(form.get("q") ?? "").trim() || undefined,
      },
      0,
    );
  }

  const sealed = inventory?.batches.filter((b) => b.state === "sealed") ?? [];

  return (
    <section aria-labelledby="inventory-title">
      <h1 id="inventory-title">Corpus inventory</h1>
      {error && <p role="alert">{error}</p>}

      <form onSubmit={onScope} aria-label="Inventory scope">
        <label>
          Batch id <input name="batch" defaultValue={scope.batch_id ?? ""} />
        </label>
        <span> or </span>
        <label>
          Engagement id <input name="engagement" defaultValue={scope.engagement_id ?? ""} />
        </label>
        <label>
          Corpus Run id <input name="run" defaultValue={scope.run_id ?? ""} />
        </label>
        <button type="submit">Show inventory</button>
      </form>

      {inventory && (
        <>
          <p data-testid="inventory-counts">
            {inventory.total} items:{" "}
            {CATEGORIES.map((c) => `${inventory.counts[c] ?? 0} ${c}`).join(", ")}
            {inventory.superseded_attempts > 0 &&
              ` · ${inventory.superseded_attempts} earlier attempt(s) superseded by retries`}
          </p>

          <table aria-label="Batches">
            <thead>
              <tr>
                <th>Batch</th>
                <th>State</th>
                <th>Entries</th>
                <th>Declared</th>
                <th>Uploads in progress</th>
                <th>Stored</th>
                <th>Immutable inventory</th>
              </tr>
            </thead>
            <tbody>
              {inventory.batches.map((b) => (
                <tr key={b.batch_id}>
                  <td>{b.batch_key}</td>
                  <td>{b.state}</td>
                  <td>{b.entries}</td>
                  <td>{b.declared_files ?? ""}</td>
                  <td>
                    {b.uploads_in_progress > 0
                      ? `${b.uploads_in_progress} (${size(b.upload_bytes_received)} of ${size(b.upload_bytes_expected)})`
                      : "0"}
                  </td>
                  <td>{size(b.bytes_stored)}</td>
                  <td>
                    {b.inventory_sha256 ? (
                      <>
                        {b.inventory_sha256.slice(0, 12)}{" "}
                        <a href={manifestExportUrl(b.batch_id, "csv")}>CSV</a>{" "}
                        <a href={manifestExportUrl(b.batch_id, "json")}>JSON</a>
                      </>
                    ) : (
                      "not sealed"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <form onSubmit={onFilter} aria-label="Inventory filters">
            <label>
              Category{" "}
              <select name="category" defaultValue={filters.category ?? ""}>
                <option value="">Any</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c} ({inventory.counts[c] ?? 0})
                  </option>
                ))}
              </select>
            </label>
            <label>
              Status{" "}
              <select name="entry_status" defaultValue={filters.entry_status ?? ""}>
                <option value="">Any</option>
                <option value="accepted">accepted</option>
                <option value="duplicate">duplicate</option>
                <option value="quarantined">quarantined</option>
                <option value="rejected">rejected</option>
              </select>
            </label>
            <label>
              Access{" "}
              <select name="access_classification" defaultValue={filters.access_classification ?? ""}>
                <option value="">Any</option>
                <option value="public">public</option>
                <option value="internal">internal</option>
                <option value="confidential">confidential</option>
                <option value="restricted">restricted</option>
              </select>
            </label>
            <label>
              <input name="pending_review" type="checkbox" defaultChecked={filters.pending_review} />{" "}
              Similarity candidates awaiting review
            </label>
            <label>
              <input name="retryable" type="checkbox" defaultChecked={filters.retryable} /> Retryable
              failures
            </label>
            <label>
              Path contains <input name="q" defaultValue={filters.q ?? ""} />
            </label>
            <button type="submit">Filter</button>
          </form>

          <p data-testid="inventory-shown">
            Showing {inventory.items.length === 0 ? 0 : offset + 1}–{offset + inventory.items.length}{" "}
            of {inventory.filtered} matching ({inventory.total} in scope)
          </p>

          <table aria-label="Inventory">
            <thead>
              <tr>
                <th>Path</th>
                <th>Batch</th>
                <th>Status</th>
                <th>Category</th>
                <th>Reason</th>
                <th>Size</th>
                <th>Rights</th>
                <th>Duplicates</th>
                <th>Review</th>
              </tr>
            </thead>
            <tbody>
              {inventory.items.map((item) => (
                <tr key={`${item.batch_id}:${item.path}`}>
                  <td>{item.path}</td>
                  <td>{item.batch_key}</td>
                  <td>{item.entry_status ?? "not received"}</td>
                  <td>{item.category}</td>
                  <td>
                    {item.reason_code ? `${item.reason_code}: ` : ""}
                    {item.reason ?? ""}
                    {item.retryable ? " (retryable)" : ""}
                  </td>
                  <td>{size(item.byte_size)}</td>
                  <td>
                    {item.access_classification
                      ? `${item.access_classification}, provider exposure ${item.provider_exposure}${item.legal_hold ? ", legal hold" : ""}`
                      : ""}
                  </td>
                  <td>
                    {item.duplicate_of_path ? `copy of ${item.duplicate_of_path}` : ""}
                    {item.occurrences > 1 ? ` (${item.occurrences} occurrences)` : ""}
                  </td>
                  <td>
                    {item.candidates_pending_review > 0
                      ? `${item.candidates_pending_review} candidate(s) to review`
                      : ""}
                    {item.lineage_links > 0 ? ` · ${item.lineage_links} lineage link(s)` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="Pages">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => void load(scope, filters, Math.max(0, offset - PAGE))}
            >
              Previous
            </button>
            <button
              type="button"
              disabled={offset + PAGE >= inventory.filtered}
              onClick={() => void load(scope, filters, offset + PAGE)}
            >
              Next
            </button>
          </nav>
          {sealed.length === 0 && <p>Seal a batch to export its immutable inventory.</p>}
        </>
      )}
    </section>
  );
}
